import { useSyncExternalStore } from 'react';
import { DESKTOP_PLATFORM_NAME, type DesktopSystem, formatDeepLink } from '@boardflight/shared';
import { platformOf } from './storefront/link/here';
import { type DesktopBuild, type DesktopRelease, buildsFor, loadDesktopRelease } from './storefront/welcome/release';

/**
 * The gate every public page's words about the desktop app stand behind
 * (`docs/off-board-plan.md` §9, stage OB10).
 *
 * **The site describes only what exists.** BoardFlight for the desktop is built on every deploy
 * and released by an operator, later, with no deploy of this site: a release is the file
 * `desktop/latest.json` appearing in the download bucket (`docs/client-release-plan.md`). So there
 * is a stretch — it may be a long one — in which this site is live and there is no app anybody can
 * have. In it, no page may name one: no button, no link, no sentence, no guide.
 *
 * The rule is kept by construction instead of by care. A page asks `useDesktopApp()` and gets a
 * release or null, and every sentence, link and guide that depends on the app is written against
 * that value — `release ? <the app's version> : <today's>`. Null is also the answer *until* the
 * manifest has been read, so the page a visitor sees first is always one that is true without the
 * app; the day a release is promoted, the same pages say more, by themselves.
 *
 * The manifest's reader is the library's (`storefront/welcome/release.ts`) and so is the question
 * "which system is this browser on" (`storefront/link/here.ts`). This module adds no second copy of
 * either: it holds the answer where a public page can read it, and the decisions `/get` makes
 * with it, as functions a test can call without a browser.
 */

export type { DesktopBuild, DesktopRelease };

/** `/get` — the download page, and with no release the no-app install route. */
export const GET_PATH = '/get';
/** `/install` — a game onto a Board by hand. True with or without the app, and linked from out there. */
export const INSTALL_PATH = '/install';

/** `boardflight://open` — bring the app forward on a computer that has it (`apps/desktop/README.md`). */
export const OPEN_APP_LINK = formatDeepLink({ kind: 'open' });

/**
 * The three systems the app is downloaded for, in the order every list of them is drawn. A phone
 * build of the same app (`ios`, `android`) is no download and is in none of these.
 */
export const SYSTEMS: readonly DesktopSystem[] = ['macos', 'windows', 'linux'];

export const SYSTEM_NAME: Record<DesktopSystem, string> = {
  macos: DESKTOP_PLATFORM_NAME.macos,
  windows: DESKTOP_PLATFORM_NAME.windows,
  linux: DESKTOP_PLATFORM_NAME.linux,
};

/**
 * What to do with the installer once it has landed, by system.
 *
 * **Nobody has watched any of this happen.** The app has been built and run on a Mac from a
 * developer's own build, and compiled for the other two by CI; no downloaded copy has been opened
 * on any of the three. So each line gives the ordinary step for that kind of file, and anything a
 * system might ask on the way is an "if" or a "may" — never a description of a prompt as though it
 * had been seen. The Windows line names what an unsigned installer usually meets
 * (`docs/off-board-plan.md` decision 15); the day the build is signed, that sentence goes.
 */
export const AFTER_DOWNLOAD: Record<DesktopSystem, string> = {
  macos:
    'Open the file you downloaded, drag BoardFlight into Applications and open it from there. If your Mac asks whether to open an app downloaded from the internet, choose Open.',
  windows:
    'Open the file you downloaded to install BoardFlight. The Windows version is not signed yet, so Windows may say it does not recognise the app. If it does, choose More info, then Run anyway.',
  linux: 'Run the AppImage (you may have to make it executable first), or install the .deb.',
};

/** One system's installers in a release, the one to offer first at the front. */
export interface SystemDownloads {
  platform: DesktopSystem;
  /** `macOS`, `Windows`, `Linux`. */
  name: string;
  /** Never empty. */
  builds: DesktopBuild[];
}

/** Every system this release has an installer for. A system it was not built for is not listed. */
export function systemsIn(release: DesktopRelease | null): SystemDownloads[] {
  return SYSTEMS.map((platform) => ({ platform, name: SYSTEM_NAME[platform], builds: buildsFor(release, platform) })).filter(
    (system) => system.builds.length > 0,
  );
}

/** "macOS", "macOS and Windows", "macOS, Windows and Linux". */
export function inWords(names: readonly string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/**
 * What `/get` is, for one visitor.
 *
 * - `install` — nothing is released. The page is the no-app route and says nothing of an app.
 * - `download` — there is an installer for this visitor's system: it first, the other systems
 *   after it.
 * - `computer` — there is an app and nothing here to hand over: a phone or a tablet, a system
 *   nobody recognised (`platform` null), or a computer this release was not built for. The page
 *   says where the app goes, and keeps the installers one press away for the visitor it misread.
 */
export type GetView =
  | { kind: 'install' }
  | { kind: 'download'; version: string; system: SystemDownloads; others: SystemDownloads[] }
  | { kind: 'computer'; version: string; platform: DesktopSystem | null; systems: SystemDownloads[] };

export function getView(release: DesktopRelease | null, platform: DesktopSystem | null): GetView {
  if (!release) return { kind: 'install' };
  const systems = systemsIn(release);
  // A manifest with no installer anybody can use is not a release (`parseDesktopRelease` already
  // answers null for one); held here too, so this function never offers an empty page.
  if (systems.length === 0) return { kind: 'install' };
  const system = platform ? systems.find((candidate) => candidate.platform === platform) : undefined;
  if (!system) return { kind: 'computer', version: release.version, platform, systems };
  return { kind: 'download', version: release.version, system, others: systems.filter((candidate) => candidate !== system) };
}

/**
 * The words on a button that leads to `/get`: the visitor's own system when there is an installer
 * for it, and no system at all otherwise — a phone is not told there is a BoardFlight "for" it.
 * Null with no release: there is no such button.
 */
export function getAppLabel(release: DesktopRelease | null, platform: DesktopSystem | null): string | null {
  const view = getView(release, platform);
  if (view.kind === 'install') return null;
  return view.kind === 'download' ? `Get BoardFlight for ${view.system.name}` : 'Get BoardFlight';
}

/* ------------------------------------------------------------------------------ the answer */

/**
 * What is known about a release right now. `settled` is false until the manifest has answered —
 * or failed to, or kept a page waiting past `RELEASE_PATIENCE_MS`, each of which is an answer —
 * and `release` is null for both "not yet" and "none".
 */
export interface DesktopAppAnswer {
  settled: boolean;
  release: DesktopRelease | null;
}

const UNASKED: DesktopAppAnswer = { settled: false, release: null };

/**
 * How long a page waits to hear whether there is an app before it carries on as though there were
 * none. Only `/get` and a guide about the app draw nothing in the meantime, and a download bucket
 * that never answers must not leave either of them on "One moment…". An answer that arrives after
 * this still counts: the page says more when it does.
 */
export const RELEASE_PATIENCE_MS = 2_500;

let answer: DesktopAppAnswer = UNASKED;
let asked = false;
const listeners = new Set<() => void>();

/**
 * Record the answer and tell every page that is showing one. What the manifest's reader calls
 * when it comes back — and what a test calls to stand a page up in either world.
 */
export function settleDesktopApp(release: DesktopRelease | null): void {
  answer = { settled: true, release };
  for (const listener of listeners) listener();
}

/** For tests: back to "nobody has asked". */
export function forgetDesktopApp(): void {
  answer = UNASKED;
  asked = false;
  for (const listener of listeners) listener();
}

/**
 * Be told when the answer changes — and, for the first watcher, go and get it.
 *
 * Asked by the first page that wants to know, in the browser only: a subscription is an effect,
 * so a render with no DOM (the tests, a crawler's first byte) asks nothing and says nothing.
 */
export function watchDesktopApp(listener: () => void): () => void {
  listeners.add(listener);
  if (!asked) {
    asked = true;
    const patience = setTimeout(() => {
      if (!answer.settled) settleDesktopApp(null);
    }, RELEASE_PATIENCE_MS);
    const heard = (release: DesktopRelease | null) => {
      clearTimeout(patience);
      settleDesktopApp(release);
    };
    // `loadDesktopRelease` answers null for every failure and is asked once per page load.
    void loadDesktopRelease().then(heard, () => heard(null));
  }
  return () => {
    listeners.delete(listener);
  };
}

/** The answer as it stands. The same object until it changes, so a reader can compare it. */
export const desktopAppAnswer = (): DesktopAppAnswer => answer;

export interface DesktopApp extends DesktopAppAnswer {
  /** Which of the three systems this browser is on, or null — a phone, a tablet, anything else. */
  platform: DesktopSystem | null;
}

/**
 * The released desktop app as this page may speak of it: null until the manifest says there is
 * one, and null for good when it says there is not.
 */
export function useDesktopApp(): DesktopApp {
  const { settled, release } = useSyncExternalStore(watchDesktopApp, desktopAppAnswer, desktopAppAnswer);
  return { settled, release, platform: platformOf(typeof navigator === 'undefined' ? null : navigator) };
}
